import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../../components/ui/Modal.js';

/**
 * A top-down flowchart, drawn the way Mermaid draws one: rounded boxes for
 * steps, diamonds for decisions, pills at the ends, and curved arrows with the
 * condition written on them.
 *
 * Laid out here rather than by the Mermaid library. What it draws is always a
 * tree -- a path back into a menu is drawn as a dashed end that says so -- and
 * a tidy-tree pass places a tree exactly. Mermaid would be a large script for
 * the same picture, in its own theme rather than the portal's, and it would
 * re-lay-out the whole chart on every keystroke in the route form.
 *
 * Shown in FlowChartViewer, a window with zoom, drag to pan, and print or save
 * as PDF. Nothing here knows about calls. RouteFlow builds the tree
 * (routeTree.ts).
 */

export type FlowShape = 'start' | 'step' | 'decision' | 'end';

export interface FlowNode {
  shape: FlowShape;
  title: string;
  detail?: string | undefined;
  tone?: 'warn' | 'danger' | undefined;
  /** Drawn dashed: a path back to somewhere already on the chart. */
  loop?: boolean | undefined;
  /** Version history (docs/41 §12): what the version shown did to this step. */
  mark?: 'new' | 'changed' | undefined;
  next: Array<{ label?: string | undefined; node: FlowNode }>;
}

const TITLE_PX = 12.5;
const DETAIL_PX = 11;
const LABEL_PX = 11;
const TITLE_LINE = 16;
const DETAIL_LINE = 14;
/** Between sibling branches. */
const H_GAP = 26;
/** Between a node and what follows: room for the arrow and its label. */
const V_GAP = 66;
const PAD = 12;

type Measure = (text: string, px: number, weight?: number) => number;

/** Text width as the browser will draw it, so boxes fit their words. */
function makeMeasure(): Measure {
  const ctx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  const family = typeof document === 'undefined'
    ? 'sans-serif'
    : getComputedStyle(document.body).fontFamily || 'sans-serif';
  return (text, px, weight = 400) => {
    if (!ctx) return text.length * px * 0.56;
    ctx.font = `${weight} ${px}px ${family}`;
    return ctx.measureText(text).width;
  };
}

function wrap(text: string, max: number, px: number, weight: number, measure: Measure, maxLines: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const tried = line ? `${line} ${word}` : word;
    if (!line || measure(tried, px, weight) <= max) {
      line = tried;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  kept[maxLines - 1] = `${kept[maxLines - 1]}…`;
  return kept;
}

interface Laid {
  node: FlowNode;
  w: number;
  h: number;
  title: string[];
  detail: string[];
  kids: Array<{ label: string | undefined; labelW: number; col: number; laid: Laid }>;
  /** Width of this node's whole subtree. */
  span: number;
  /** Centre x and top y, once placed. */
  x: number;
  y: number;
}

/** A chart, placed: every node with its position, and the size of the whole. */
export interface FlowLayout {
  all: Laid[];
  width: number;
  height: number;
}

function size(node: FlowNode, measure: Measure): Laid {
  const decision = node.shape === 'decision';
  const maxText = decision ? 150 : 190;
  const title = wrap(node.title, maxText, TITLE_PX, 550, measure, 3);
  const detail = node.detail ? wrap(node.detail, maxText, DETAIL_PX, 400, measure, decision ? 2 : 3) : [];
  const textW = Math.max(
    40,
    ...title.map((l) => measure(l, TITLE_PX, 550)),
    ...detail.map((l) => measure(l, DETAIL_PX)),
  );
  const textH = title.length * TITLE_LINE + detail.length * DETAIL_LINE;
  // A diamond needs about half as much again in each direction to hold the same
  // text, because its corners are empty.
  const w = decision ? Math.max(130, textW * 1.5 + 30) : Math.max(110, textW + 30);
  const h = decision ? Math.max(76, textH * 1.6 + 26) : textH + 20;

  const kids = node.next.map((n) => {
    const laid = size(n.node, measure);
    const labelW = n.label ? measure(n.label, LABEL_PX) + 14 : 0;
    return { label: n.label, labelW, col: Math.max(laid.span, labelW), laid };
  });
  const kidsSpan = kids.reduce((s, k) => s + k.col, 0) + H_GAP * Math.max(0, kids.length - 1);
  return { node, w, h, title, detail, kids, span: Math.max(w, kidsSpan), x: 0, y: 0 };
}

function place(l: Laid, left: number, top: number): void {
  l.x = left + l.span / 2;
  l.y = top;
  const total = l.kids.reduce((s, k) => s + k.col, 0) + H_GAP * Math.max(0, l.kids.length - 1);
  let cursor = left + (l.span - total) / 2;
  for (const k of l.kids) {
    place(k.laid, cursor + (k.col - k.laid.span) / 2, top + l.h + V_GAP);
    cursor += k.col + H_GAP;
  }
}

function flatten(l: Laid, out: Laid[] = []): Laid[] {
  out.push(l);
  for (const k of l.kids) flatten(k.laid, out);
  return out;
}

export function layoutFlow(root: FlowNode): FlowLayout {
  const laid = size(root, makeMeasure());
  place(laid, PAD, PAD);
  const all = flatten(laid);
  return {
    all,
    width: Math.ceil(laid.span + PAD * 2),
    height: Math.ceil(Math.max(...all.map((l) => l.y + l.h)) + PAD),
  };
}

function Shape({ l }: { l: Laid }) {
  const { node } = l;
  const textH = l.title.length * TITLE_LINE + l.detail.length * DETAIL_LINE;
  const top = l.y + (l.h - textH) / 2;
  const shape = node.shape === 'decision'
    ? (
      <polygon className="arc-fc-shape"
        points={`${l.x},${l.y} ${l.x + l.w / 2},${l.y + l.h / 2} ${l.x},${l.y + l.h} ${l.x - l.w / 2},${l.y + l.h / 2}`} />
    )
    : (
      <rect className="arc-fc-shape" x={l.x - l.w / 2} y={l.y} width={l.w} height={l.h}
        rx={node.shape === 'step' ? 6 : Math.min(l.h / 2, 18)} />
    );

  return (
    <g className={`arc-fc-node arc-fc-${node.shape}${node.loop ? ' arc-fc-loop' : ''}`} data-tone={node.tone} data-changed={node.mark}>
      <title>{node.detail ? `${node.title}. ${node.detail}` : node.title}</title>
      {shape}
      {node.mark && (
        <g className="arc-fc-mark">
          <rect x={l.x + l.w / 2 - 50} y={l.y - 9} width={48} height={15} rx={7.5} />
          <text x={l.x + l.w / 2 - 26} y={l.y + 2} textAnchor="middle">{node.mark}</text>
        </g>
      )}
      {l.title.map((t, i) => (
        <text key={`t${i}`} className="arc-fc-title" x={l.x} y={top + (i + 1) * TITLE_LINE - 4}
          textAnchor="middle">{t}</text>
      ))}
      {l.detail.map((t, i) => (
        <text key={`d${i}`} className="arc-fc-detail" x={l.x}
          y={top + l.title.length * TITLE_LINE + (i + 1) * DETAIL_LINE - 3} textAnchor="middle">{t}</text>
      ))}
    </g>
  );
}

/** The drawing itself, at any scale: the viewBox stays put and the size moves, so zoom stays sharp. */
export function FlowChartSvg({ chart, zoom = 1, label }: { chart: FlowLayout; zoom?: number; label: string }) {
  const marker = `arc-fc-arrow-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <svg width={Math.round(chart.width * zoom)} height={Math.round(chart.height * zoom)}
      viewBox={`0 0 ${chart.width} ${chart.height}`} role="img" aria-label={label}>
      <defs>
        <marker id={marker} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path className="arc-fc-arrow" d="M 0 0 L 10 5 L 0 10 z" />
        </marker>
      </defs>

      {chart.all.map((from, fi) => from.kids.map((k, ki) => {
        const x1 = from.x;
        const y1 = from.y + from.h;
        const x2 = k.laid.x;
        const y2 = k.laid.y - 2;
        const mid = (y1 + y2) / 2;
        return (
          <g key={`e${fi}-${ki}`}>
            <path className="arc-fc-edge" markerEnd={`url(#${marker})`}
              d={`M ${x1} ${y1} C ${x1} ${mid}, ${x2} ${mid}, ${x2} ${y2}`} />
            {/* On the last, straight run into the node, where it cannot be
                mistaken for a neighbour's. */}
            {k.label && (
              <g>
                <rect className="arc-fc-label-bg" x={x2 - k.labelW / 2} y={y2 - 29} width={k.labelW}
                  height={17} rx={8.5} />
                <text className="arc-fc-label" x={x2} y={y2 - 17} textAnchor="middle">{k.label}</text>
              </g>
            )}
          </g>
        );
      }))}

      {chart.all.map((l, i) => <Shape key={`n${i}`} l={l} />)}
    </svg>
  );
}

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 3;
const clampZoom = (z: number) => Math.round(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z)) * 100) / 100;

/**
 * Prints the open chart and nothing else: landscape, in light colours whatever
 * the screen theme (the print rules in app.css). "Save as PDF" is the
 * browser's own destination in the same dialog, so there is no PDF library.
 */
function printChart(): void {
  const page = document.createElement('style');
  page.textContent = '@page { size: landscape; margin: 12mm; }';
  document.head.appendChild(page);
  document.documentElement.classList.add('arc-printing-flowchart');
  const done = () => {
    page.remove();
    document.documentElement.classList.remove('arc-printing-flowchart');
    window.removeEventListener('afterprint', done);
  };
  window.addEventListener('afterprint', done);
  window.print();
}

/**
 * The chart in a window of its own: zoom in and out, fit it to the window,
 * drag to pan, and print or save as PDF.
 *
 * Opens fitted to the window. The buttons, Ctrl or Cmd with the wheel, and the
 * + - 0 keys zoom; a plain wheel scrolls, as it does everywhere else.
 */
export function FlowChartViewer({ root, label, title, onClose }: {
  root: FlowNode; label: string; title: string; onClose: () => void;
}) {
  const chart = useMemo(() => layoutFlow(root), [root]);
  const body = useRef<HTMLDivElement>(null);
  /** null: fitted to the window, and refitted when the window changes size. */
  const [zoom, setZoom] = useState<number | null>(null);
  const [fit, setFit] = useState(1);
  const fitRef = useRef(1);
  const scale = zoom ?? fit;
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  useLayoutEffect(() => {
    const el = body.current;
    if (!el) return undefined;
    const measure = () => {
      const next = Math.max(MIN_ZOOM, Math.min(1.25,
        (el.clientWidth - 32) / chart.width, (el.clientHeight - 32) / chart.height));
      fitRef.current = next;
      setFit(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [chart.width, chart.height]);

  useEffect(() => {
    const el = body.current;
    if (!el) return undefined;
    // Native and not passive: React's wheel handler cannot stop the page zooming.
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => clampZoom((z ?? fitRef.current) * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === '+' || e.key === '=') setZoom((z) => clampZoom((z ?? fitRef.current) * 1.25));
      else if (e.key === '-') setZoom((z) => clampZoom((z ?? fitRef.current) / 1.25));
      else if (e.key === '0') setZoom(null);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('keydown', onKey);
    return () => {
      el.removeEventListener('wheel', onWheel);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <Modal title={title} onClose={onClose} size="full" actions={(
      <>
        <div className="arc-row" style={{ gap: 2 }} role="group" aria-label="Zoom">
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Zoom out"
            title="Zoom out (-)" onClick={() => setZoom(clampZoom(scale / 1.25))}>−</button>
          <span className="arc-subtle arc-num" style={{ minWidth: 44, textAlign: 'center', fontSize: 12 }}
            aria-live="polite">{Math.round(scale * 100)}%</span>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Zoom in"
            title="Zoom in (+)" onClick={() => setZoom(clampZoom(scale * 1.25))}>+</button>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-pressed={zoom === null}
            title="Fit to the window (0)" onClick={() => setZoom(null)}>Fit</button>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-pressed={zoom === 1}
            onClick={() => setZoom(1)}>100%</button>
        </div>
        <button type="button" className="arc-btn arc-btn-sm" onClick={printChart}>
          Print or save as PDF
        </button>
      </>
    )}>
      <div ref={body} className="arc-fc-viewport" data-dragging={dragging}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          const el = e.currentTarget;
          drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
          el.setPointerCapture(e.pointerId);
          setDragging(true);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          e.currentTarget.scrollLeft = d.left - (e.clientX - d.x);
          e.currentTarget.scrollTop = d.top - (e.clientY - d.y);
        }}
        onPointerUp={(e) => {
          drag.current = null;
          setDragging(false);
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
        onPointerCancel={() => { drag.current = null; setDragging(false); }}>
        <FlowChartSvg chart={chart} zoom={scale} label={label} />
      </div>
    </Modal>
  );
}
